import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ExternalLink, Loader2 } from "lucide-react";

export const InAppBrowser = ({ app, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!app) return;
    setLoaded(false);
    setBlocked(false);
    loadedRef.current = false;

    // Smart fallback: if the site refuses to embed (X-Frame-Options / CSP)
    // or isn't reachable, the load event won't fire in time -> open externally.
    const timer = setTimeout(() => {
      if (!loadedRef.current) {
        setBlocked(true);
        window.open(app.url, "_blank", "noopener,noreferrer");
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [app]);

  const handleLoad = () => {
    loadedRef.current = true;
    setLoaded(true);
  };

  return (
    <AnimatePresence>
      {app && (
        <motion.div
          data-testid="in-app-browser"
          className="absolute inset-0 z-50 flex flex-col bg-white"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
        >
          <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-black/5 bg-[#051833] px-3 text-white">
            <button
              type="button"
              data-testid="browser-back-btn"
              onClick={onClose}
              className="flex items-center gap-1 rounded-full px-2 py-1.5 text-sm font-medium text-white/90 active:scale-95"
            >
              <ChevronLeft size={20} strokeWidth={2.2} />
              Done
            </button>

            <div className="flex flex-col items-center leading-none">
              <span className="text-[13px] font-semibold">{app.name}</span>
              <span className="mt-0.5 text-[10px] text-white/50">
                {new URL(app.url).hostname}
              </span>
            </div>

            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="browser-external-btn"
              aria-label="Open in new tab"
              className="rounded-full p-2 text-white/90 active:scale-95"
            >
              <ExternalLink size={18} strokeWidth={2.2} />
            </a>
          </div>

          <div className="relative flex-1 bg-white">
            {blocked ? (
              <div
                data-testid="browser-fallback"
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-[#051833] to-[#0b4c8c] px-8 text-center"
              >
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/10 backdrop-blur-md">
                  <ExternalLink size={26} className="text-white" />
                </div>
                <div>
                  <p className="text-base font-semibold text-white">
                    Opening in your browser
                  </p>
                  <p className="mx-auto mt-2 max-w-[260px] text-sm text-white/60">
                    {app.name} can’t be shown inside the app, so we’ve opened it
                    in a new browser tab.
                  </p>
                </div>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="browser-fallback-open-btn"
                  className="mt-1 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#0b4c8c] active:scale-95"
                >
                  Open {app.name}
                </a>
                <button
                  type="button"
                  data-testid="browser-fallback-back-btn"
                  onClick={onClose}
                  className="text-sm text-white/60 underline-offset-4 hover:underline"
                >
                  Back to home
                </button>
              </div>
            ) : (
              <>
                {!loaded && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white px-8 text-center">
                    <Loader2 size={28} className="animate-spin text-[#1B9DE0]" />
                    <p className="text-sm text-slate-500">Loading {app.name}…</p>
                  </div>
                )}
                <iframe
                  key={app.id}
                  title={app.name}
                  src={app.url}
                  onLoad={handleLoad}
                  className="h-full w-full border-none"
                  allow="camera; microphone; clipboard-write; fullscreen"
                />
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
